import { useId } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { LOGIC, type Logic } from "../constants";
import type { SegmentFormValues } from "../schemas";
import { LogicToggle } from "./LogicToggle";

import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";

type GlobalCombineBlockProps = {
  readOnly: boolean;
};

export function GlobalCombineBlock({ readOnly }: GlobalCombineBlockProps) {
  const { t } = useTranslation();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<SegmentFormValues>();
  const type = watch("type");
  const errorId = useId();
  const combineRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.COMBINE);
  const typeMessage = errors.type?.message;

  const handleChange = (next: Logic) =>
    setValue("type", next, { shouldDirty: true, shouldValidate: true });

  return (
    <div
      ref={combineRef}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-transparent px-4 py-3 md:px-6"
    >
      <div className="grid gap-0.5">
        <span className="text-sm font-semibold">{t("admin.segments.builder.combine.title")}</span>
        {type && (
          <span className="text-sm text-muted-foreground">
            {type === LOGIC.AND
              ? t("admin.segments.builder.combine.all")
              : t("admin.segments.builder.combine.any")}
          </span>
        )}
        {typeMessage && (
          <span id={errorId} role="alert" className="text-sm text-destructive">
            {typeMessage}
          </span>
        )}
      </div>
      <LogicToggle
        value={type}
        onChange={handleChange}
        label={t("admin.segments.builder.combine.label")}
        readOnly={readOnly}
        aria-invalid={!!typeMessage}
        aria-describedby={typeMessage ? errorId : undefined}
      />
    </div>
  );
}
